<template>
    <div class="hmi-card-float" :class="{ 'is-static': !animated || !status }">
        <div
            ref="card"
            class="hmi-card"
            :class="{ 'is-inactive': !status }"
            @pointermove="onTilt"
            @pointerleave="resetTilt"
            @pointerup="resetTilt"
            @pointercancel="resetTilt"
        >
            <!-- Logo, branding and artwork: active cards only -->
            <template v-if="status">
                <img class="hc-art" src="../../../assets/white-cross.webp" alt="">
                <img class="hc-icon" src="../../../assets/hmi-cross.webp" alt="HMI logo">
                <div class="hc-t hc-word">
                    HMI
                </div>
                <div class="hc-t hc-sub">
                    Health Maintenance, Inc.
                </div>
                <div class="hc-t hc-tag">
                    The Philippines' First HMO
                </div>
                <div class="hc-t hc-est">
                    Established 1981
                </div>
            </template>

            <div ref="name" class="hc-t hc-name">
                {{ name }}
            </div>
            <div ref="company" class="hc-t hc-company">
                {{ companyName }}
            </div>
            <div class="hc-t hc-d hc-d1">
                MEMBER NO.: <span>{{ memberNumber }}</span>
            </div>
            <div class="hc-t hc-d hc-d2">
                CARD NO.: <span>{{ cardNo || '•••• •••• •••••• ••' }}</span>
            </div>
            <div class="hc-t hc-d hc-d3">
                DOB: <span>{{ dob }}</span> &nbsp;SEX: <span>{{ sex }}</span>
            </div>
            <div v-if="dental" class="hc-t hc-d hc-d4">
                DENTAL: <span>{{ dental }}</span>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * HMI digital ID card.
 *
 * Usage:
 *   <HmiCard
 *       name="DELA CRUZ, JUAN A."
 *       company-name="COMPANY NAME"
 *       member-number="999999"
 *       card-no="1981-0000-999999-01"
 *       dob="01/01/1980"
 *       sex="M"
 *       dental="DN TEL. 8123-4444 & 8567-8888"
 *       :status="true"
 *       :animated="true"
 *   />
 *
 * status   true  = active card (green, logo, artwork)
 *          false = plain grey card without logo
 * animated false = no floating / light sweep / tilt (handy for long lists of cards)
 */
export default {
    name: 'HmiCard',
    props: {
        name: { type: String, default: '' },
        companyName: { type: String, default: '' },
        memberNumber: { type: [String, Number], default: '' },
        cardNo: { type: String, default: '' },
        dob: { type: String, default: '' },
        sex: { type: String, default: '' },
        dental: { type: String, default: '' },
        status: { type: Boolean, default: false },
        animated: { type: Boolean, default: true }
    },
    data: () => ({
        reduceMotion: false
    }),
    watch: {
        name() { this.$nextTick(this.fitText) },
        companyName() { this.$nextTick(this.fitText) }
    },
    mounted() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        this.fitText()
        // re-fit when fonts finish loading and whenever the card changes size
        // (also covers cards that were hidden, e.g. inside a tab or dialog, when mounted)
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fitText)
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.fitText())
            this.resizeObserver.observe(this.$refs.card)
        } else {
            window.addEventListener('resize', this.fitText)
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
        window.removeEventListener('resize', this.fitText)
    },
    methods: {
        // 3D tilt + glare that follows the finger / mouse
        onTilt(e) {
            const el = this.$refs.card
            if (!el || !this.animated || this.reduceMotion) return
            const r = el.getBoundingClientRect()
            const px = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1)
            const py = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1)
            const MAX = 14 // max tilt in degrees
            el.style.setProperty('--ry', `${(px - 0.5) * MAX}deg`)
            el.style.setProperty('--rx', `${(0.5 - py) * MAX}deg`)
            el.style.setProperty('--gx', `${px * 100}%`)
            el.style.setProperty('--gy', `${py * 100}%`)
            el.style.setProperty('--glare', '1')
        },
        resetTilt() {
            const el = this.$refs.card
            if (!el) return
            ;['--rx', '--ry', '--glare'].forEach((k) => el.style.removeProperty(k))
        },

        // Shrinks long name / company text so it always fits inside the card.
        // Short text keeps its size, long text shrinks down to MIN_SCALE, and only
        // beyond that is it cut with "..." (see .hc-name / .hc-company in the CSS).
        fitText() {
            const card = this.$refs.card
            if (!card) return
            const width = card.clientWidth
            if (!width) return // hidden right now; the ResizeObserver calls us again when shown
            const AVAILABLE = 0.852 // usable width as a fraction of the card width
            const MIN_SCALE = 0.55
            // [element ref, normal font size in cqw - same numbers as .hc-name / .hc-company in the CSS]
            ;[['name', 4.77], ['company', 3.83]].forEach(([ref, base]) => {
                const el = this.$refs[ref]
                if (!el) return
                // set the finished font-size straight on the element (no CSS variable or calc() involved)
                const set = (s) => { el.style.fontSize = `${(base * s).toFixed(3)}cqw` }
                const floor3 = (n) => Math.floor(n * 1000) / 1000

                set(1) // measure at full size
                let scale = Math.min(1, (width * AVAILABLE) / (el.scrollWidth || 1))
                scale = Math.max(MIN_SCALE, floor3(scale))
                set(scale)

                // safety net: if the browser still reports clipped text
                // (different font, rounding...), shrink in small steps
                let guard = 0
                while (el.scrollWidth > el.clientWidth + 1 && scale > MIN_SCALE && guard++ < 15) {
                    scale = Math.max(MIN_SCALE, floor3(scale * 0.97))
                    set(scale)
                }
            })
        }
    }
}
</script>

<style scoped>
/* ================= Wrapper (idle floating) ================= */
.hmi-card-float {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  perspective: 900px;
  animation: hc-float 6s ease-in-out infinite;
}
@keyframes hc-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* ================= Card ================= */
.hmi-card {
  --ink: #111;
  position: relative;
  transform: rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
  transition: transform .25s ease-out;
  touch-action: pan-y; /* vertical scroll still works on phones */
  will-change: transform;
  width: 100%;
  max-width: 560px;
  aspect-ratio: 85.6 / 54;
  border-radius: 18px;
  overflow: hidden;
  container-type: inline-size; /* every cqw below = 1% of the card width */
  background: linear-gradient(to bottom, #fff 0%, #fff 13%, #b9dcc0 28%, #7cc088 43%, #3a9d4c 62%, #1f7e35 80%, #187030 100%);
  box-shadow: 0 6px 18px rgba(0, 0, 0, .25);
  font-family: "Century Gothic", CenturyGothic, "Apple Gothic", Arial, sans-serif;
  color: var(--ink);
}

/* glare that follows the pointer */
.hmi-card::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: radial-gradient(circle at var(--gx, 50%) var(--gy, 0%), rgba(255, 255, 255, .28), transparent 55%);
  opacity: var(--glare, 0);
  transition: opacity .3s;
}

/* light sweep that crosses the card every few seconds */
.hmi-card::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(115deg, transparent 38%, rgba(255, 255, 255, .32) 50%, transparent 62%);
  transform: translateX(-120%);
  animation: hc-shine 5s ease-in-out 1s infinite;
}
@keyframes hc-shine {
  0%, 55% { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
}

/* still card: animated="false", inactive card, or "reduce motion" */
.is-static,
.is-static .hmi-card::after { animation: none; }
.is-static .hmi-card { transition: none; transform: none; }
@media (prefers-reduced-motion: reduce) {
  .hmi-card-float,
  .hmi-card::after { animation: none; }
  .hmi-card { transition: none; }
}

/* ================= Text and images ================= */
.hmi-card > .hc-t {
  position: absolute;
  transform: translateY(-50%);
  white-space: nowrap;
  line-height: 1;
  font-weight: 700;
  z-index: 1;
  text-align: left;
  margin: 0;
}

/* logo and branding */
.hc-icon { position: absolute; z-index: 1; left: 7.4cqw; top: 4.87cqw; width: 10.3cqw; }
.hc-art  { position: absolute; z-index: 0; left: 69.4cqw; top: 26.2cqw; width: 45cqw; height: auto; }
.hc-word { left: 18.0cqw; top: 9.8cqw; font-size: 9.03cqw; letter-spacing: .1cqw; color: #1f2a2a; }
.hc-sub  { left: 18.7cqw; top: 14.97cqw; font-size: 1.97cqw; color: #1f2a2a; }
.hc-tag  { left: 18.7cqw; top: 16.85cqw; font-size: 1.32cqw; font-style: italic; color: #1f2a2a; }
.hmi-card > .hc-est {
  left: 59.4cqw; top: 13.86cqw;
  font-family: "Brush Script MT", "Brush Script Std", cursive;
  font-weight: 400; font-style: normal;
  font-size: 4.4cqw; letter-spacing: .25cqw; color: #3a8a4d;
}

/* name and company (fitText() lowers the font size for long names) */
.hc-name    { left: 7.4cqw; top: 22.82cqw; font-size: 4.77cqw; }
.hc-company { left: 7.4cqw; top: 28.08cqw; font-size: 3.83cqw; }
.hc-name, .hc-company {
  max-width: 88cqw; /* a little wider than the fit target (85.2cqw) so rounding never triggers the "..." */
  overflow: hidden;
  text-overflow: ellipsis; /* last resort when even the smallest size does not fit */
  padding: .12em 0; /* keeps commas / descenders from being clipped */
}

/* member details */
.hc-d  { left: 7.4cqw; font-size: 3.33cqw; }
.hc-d1 { top: 34.91cqw; }
.hc-d2 { top: 40.49cqw; }
.hc-d3 { top: 46.17cqw; }
.hc-d4 { top: 51.95cqw; }

/* ================= Inactive card: plain, flat, text centered vertically ================= */
.hmi-card.is-inactive {
  background: #e9e9e9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
  color: #222;
}
.hmi-card.is-inactive .hc-name    { top: 19cqw; }
.hmi-card.is-inactive .hc-company { top: 24.3cqw; }
.hmi-card.is-inactive .hc-d1      { top: 31.1cqw; }
.hmi-card.is-inactive .hc-d2      { top: 36.7cqw; }
.hmi-card.is-inactive .hc-d3      { top: 42.4cqw; }
.hmi-card.is-inactive .hc-d4      { top: 48.1cqw; }
</style>
